// app/components/home/VideoCover.tsx
"use client";

import { useEffect, useState, useRef, useLayoutEffect } from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Button, Box, Grid } from "@mui/material";
import { useLocale, useTranslations } from "next-intl";
import CallMadeIcon from "@mui/icons-material/CallMade";
import Hls from "hls.js";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string; // ใช้ map override ตาม key เช่น "home.hero.title"
};

type HLSVideoProps = {
  src: string;
  fallbackSrc?: string; // ✅ URL วีดีโอ MP4 สำหรับ fallback
};

const HLSVideo = ({ src, fallbackSrc }: HLSVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [supportState, setSupportState] = useState<
    "init" | "hls" | "native" | "fallback" | "unsupported"
  >("init");

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // เคลียร์ src เดิมก่อน
    video.removeAttribute("src");
    video.load();

    if (Hls.isSupported()) {
      // ✅ ใช้ hls.js
      const hls = new Hls();
      hls.loadSource(src);
      hls.attachMedia(video);
      setSupportState("hls");

      return () => {
        hls.destroy();
      };
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // ✅ Safari / iOS เล่น HLS ได้แบบ native
      video.src = src;
      setSupportState("native");
    } else if (fallbackSrc) {
      // ✅ กรณีไม่รองรับ HLS → ใช้ MP4 fallback
      console.warn("[HLSVideo] HLS not supported. Using fallback MP4:", fallbackSrc);
      video.src = fallbackSrc;
      setSupportState("fallback");
    } else {
      // ❌ ไม่รองรับ + ไม่มี fallback → แจ้งข้อความ
      console.warn("[HLSVideo] HLS is not supported in this browser.");
      setSupportState("unsupported");
    }
  }, [src, fallbackSrc]);

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <video
        ref={videoRef}
        width="100%"
        height="auto"
        preload="none"
        autoPlay
        loop
        muted
        playsInline
      >
        Your browser does not support the video tag.
      </video>

      {supportState === "unsupported" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            textAlign: "center",
            background: "linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0.8))",
          }}
        >
          <Typography variant="body1" sx={{ color: "white" }}>
            Your browser cannot play this background video.
            <br />
            Please use a modern browser such as Chrome, Safari, Edge, or Firefox.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const PAGE_PATH = "/"; // path ของหน้า Home หลังตัด /en /zh ออก

// helper: normalize line-break ให้รองรับทั้ง \n, \r\n และ <br>
function normalizeLineBreaks(input: string | undefined | null): string {
  if (!input) return "";
  // 1) <br>, <br/>, <br /> → \n
  const withNewlines = input.replace(/<br\s*\/?>/gi, "\n");
  // 2) normalize \r\n / \r → \n
  return withNewlines.replace(/\r\n|\r/g, "\n");
}

export default function VideoCover() {
  const t = useTranslations("Home");
  const m = useTranslations("Menu");
  const locale = useLocale();

  // ---- ค่า default จาก i18n (ใช้เป็น SSR + fallback ดีต่อ SEO) ----
  const defaultTitle = normalizeLineBreaks(t("Hero.title"));
  const defaultSubtitle = normalizeLineBreaks(t("Hero.subtitle"));
  const defaultBody = normalizeLineBreaks(`${t("Hero.subtitle2")} ${t("Hero.subtitle3")}`);

  // ---- state ที่ใช้แสดงบนจอจริง ----
  const [heroTitle, setHeroTitle] = useState(defaultTitle);
  const [heroSubtitle, setHeroSubtitle] = useState(defaultSubtitle);
  const [heroBody, setHeroBody] = useState(defaultBody);

  // ✅ เนียนสุด: ซ่อนข้อความไว้ก่อน แล้วค่อย fade-in เมื่อ inline พร้อม
  const [inlineReady, setInlineReady] = useState(false);

  // reset เป็นค่า i18n เฉพาะตอน locale เปลี่ยน (กัน flash จาก deps อื่น)
  useEffect(() => {
    setHeroTitle(defaultTitle);
    setHeroSubtitle(defaultSubtitle);
    setHeroBody(defaultBody);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  // โหลด inline edits จาก CMS ผ่าน proxy → set ครบก่อนแล้วค่อยโชว์
  useLayoutEffect(() => {
    let cancelled = false;

    setInlineReady(false);

    // กันหน้าว่างค้าง: ถ้า inline ช้า/ล่ม เกิน 1200ms ให้โชว์ i18n ไปก่อน
    const fallbackTimer = setTimeout(() => {
      if (!cancelled) setInlineReady(true);
    }, 1200);

    const fetchInline = async () => {
      try {
        const websiteId = process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID || "";

        const params = new URLSearchParams({
          websiteId,
          path: PAGE_PATH,
          locale: String(locale),
        });

        const res = await fetch(`/api/admin/page-inline?${params.toString()}`, {
          credentials: "include",
          cache: "no-store",
        });

        if (!res.ok) {
          if (!cancelled) setInlineReady(true);
          return;
        }

        const data = await res.json();
        const list: InlineEdit[] = data?.edits || [];

        const map: Record<string, string> = {};
        for (const e of list) {
          if (e.cmsKey) map[e.cmsKey] = normalizeLineBreaks(e.value);
        }

        if (cancelled) return;

        // ✅ set ให้ครบก่อน “ค่อยโชว์” (ไม่เห็นจังหวะเปลี่ยน)
        setHeroTitle(map["home.hero.title"] ?? defaultTitle);
        setHeroSubtitle(map["home.hero.subtitle"] ?? defaultSubtitle);
        setHeroBody(map["home.hero.body"] ?? defaultBody);

        setInlineReady(true);
      } catch (err) {
        console.warn("[VideoCover] inline fetch error:", err);
        if (!cancelled) setInlineReady(true);
      }
    };

    fetchInline();

    return () => {
      cancelled = true;
      clearTimeout(fallbackTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  // ---- EN/TH → /contact-us, ZH → /zh/contact-us ----
  const contactHref = locale === "zh" ? "/zh/contact-us" : "/contact-us";

  // ✅ URL ของ HLS + Fallback MP4 — ใช้ path แบบ relative ผ่าน rewrite /assets → CDN
  // (same-origin จึงไม่ติด CORS; ห้ามชี้ตรงไป CDN เพราะ CDN ไม่ส่ง
  //  Access-Control-Allow-Origin แล้ว hls.js ที่โหลดด้วย XHR จะโดนบล็อกทั้ง playlist)
  const HLS_SRC = "/assets/home/video.m3u8";
  const FALLBACK_MP4_SRC = "/assets/home/video-fallback.mp4";

  return (
    <Box className="video-wrap">
      {/* Video Container */}
      <Box
        className="home-video"
        sx={{
          position: "relative",
          overflow: "hidden",
        }}
      >
        <HLSVideo src={HLS_SRC} fallbackSrc={FALLBACK_MP4_SRC} />

        {/* Gradient Overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(20,20,20,0.5) 100%)",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
      </Box>
      {/* Content */}
      <Box
        className="video-content"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container className="container">
          {/* ✅ Fade-in ครั้งเดียว หลัง inline พร้อม */}
          <Box
            sx={{
              opacity: inlineReady ? 1 : 0,
              transform: inlineReady ? "none" : "translateY(4px)",
              transition: "opacity 220ms ease, transform 220ms ease",
              willChange: "opacity, transform",
            }}
          >
            <Grid container spacing={0}>
              <Grid size={{ xs: 12, md: 6.8 }}>
                <Typography
                  variant="h1"
                  data-heading-tag="H1"
                  data-aos="zoom-in"
                  data-aos-offset="0"
                  data-cms-key="home.hero.title"
                  sx={{
                    mb: 3,
                    whiteSpace: "pre-line",
                    color: "white"
                  }}>
                  {heroTitle}
                </Typography>

                <Typography
                  variant="h3"
                  data-heading-tag="H3"
                  data-aos="zoom-in"
                  data-aos-offset="-100"
                  data-cms-key="home.hero.subtitle"
                  sx={{ whiteSpace: "pre-line", color: "white" }}
                >
                  {heroSubtitle}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 5.2 }}>
                <Typography
                  variant="body1"
                  sx={{ mb: 3, whiteSpace: "pre-line", color: "white" }}
                  data-aos="zoom-in"
                  data-aos-offset="-150"
                  data-cms-key="home.hero.body"
                >
                  {heroBody}
                </Typography>

                <Button
                  color="primary"
                  variant="contained"
                  className="video-btn"
                  endIcon={<CallMadeIcon />}
                  size="large"
                  data-aos="zoom-in"
                  data-aos-offset="-150"
                  href={contactHref}
                >
                  {m("Contact")}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
